import React, { CSSProperties } from "react";

import { useLingui } from "@lingui/react";

import { TableHead as MuiTableHead, Typography } from "@mui/material";
import styled from "@mui/material/styles/styled";
import { TableHeadProps as MuiTableHeadProps } from "@mui/material/TableHead/TableHead";

import { HeadCell, TranslatedHeadCell } from "@app/common/hooks/useDataTable";
import { lightOrDark } from "@app/embedded/theme/utils";

import Checkbox from "@rui/Checkbox";
import { Row } from "@rui/Stack";
import TooltipIcon from "@rui/TooltipIcon";
import TooltipWrapper from "@rui/TooltipWrapper";

import TableCell from "./TableCell";
import TableRow from "./TableRow";
import TableSortLabel from "./TableSortLabel";

const EXPAND_ARROW_WIDTH = 44;

const StyledTableHead = styled(MuiTableHead)(({ theme }) => ({
  borderRadius: "8px 8px 0 0",
  height: "46px",
  color: lightOrDark(
    theme.palette.shades.neutral[900],
    theme.palette.shades.neutral[50]
  )(theme),
  "& .MuiTableRow-root .MuiTableCell-root": {
    height: "46px",
    padding: "0 0 0 16px",
    color: lightOrDark(
      theme.palette.shades.neutral[900],
      theme.palette.shades.neutral[50]
    )(theme),
    fontWeight: 600,
    fontSize: "0.875rem",
    lineHeight: 24 / 14,
    borderBottom: "1px solid",
    borderColor: lightOrDark(
      theme.palette.shades.neutral[100],
      theme.palette.shades.neutral[800]
    )(theme),
    "&:last-child": { paddingRight: "16px" },
  },
  "& .Mui-active": {
    color: lightOrDark(
      theme.palette.shades.neutral[900],
      theme.palette.shades.neutral[100]
    )(theme),
  },
  "&:hover .MuiTableSortLabel-root": {
    color: lightOrDark(
      theme.palette.shades.neutral[900],
      theme.palette.shades.neutral[100]
    )(theme),
  },
  "& .MuiTableSortLabel-icon.MuiSvgIcon-root": {
    color: lightOrDark(
      theme.palette.shades.secondary[300],
      theme.palette.shades.primary[400]
    )(theme),
  },
}));

interface CheckboxProps {
  numSelected: number;
  rowCount: number;
  onSelectAllClick: () => void;
}

interface TableHeadProps<T extends object> extends MuiTableHeadProps {
  headCells: TranslatedHeadCell<T>[];
  onSort: (event: React.MouseEvent, id: HeadCell<T>["id"]) => void;
  order: "asc" | "desc";
  orderBy: string | number;
  expandable?: boolean;
  checkboxProps?: CheckboxProps;
}

const ALIGN_TO_FLEX: { [key: string]: CSSProperties["justifyContent"] } = {
  left: "flex-start",
  right: "flex-end",
  center: "center",
};

const CheckAllCell = ({
  numSelected,
  rowCount,
  onSelectAllClick,
}: CheckboxProps) => {
  return (
    <TableCell padding="checkbox">
      <Checkbox
        indeterminate={numSelected > 0 && numSelected < rowCount}
        checked={rowCount > 0 && numSelected === rowCount}
        onChange={onSelectAllClick}
        inputProps={{
          "aria-label": "select all",
        }}
      />
    </TableCell>
  );
};

const TableHead = <T extends object>({
  headCells,
  onSort,
  order,
  orderBy,
  expandable,
  checkboxProps,
  sx,
}: TableHeadProps<T>) => {
  const { _ } = useLingui();
  const createSortHandler = (id: string) => (event: React.MouseEvent) => {
    onSort(event, id);
  };

  return (
    <StyledTableHead sx={sx}>
      <TableRow>
        {checkboxProps && <CheckAllCell {...checkboxProps} />}
        {headCells.map((headCell, i: number) => {
          const showTooltipIcon = headCell.tooltip && headCell.withTooltipIcon;

          if (headCell.sortable !== false) {
            return (
              <TableCell
                key={headCell.id}
                sortDirection={orderBy === headCell.id ? order : false}
                sx={{ border: "none" }}
                align={headCell.align || "left"}
              >
                <TooltipWrapper
                  title={headCell.tooltip}
                  placement="top"
                  enterDelay={1000}
                  show={!showTooltipIcon}
                >
                  <TableSortLabel
                    active={orderBy === headCell.id}
                    direction={orderBy === headCell.id ? order : "asc"}
                    onClick={createSortHandler(headCell.id)}
                    sx={{ position: "relative" }}
                  >
                    <Typography
                      fontWeight={600}
                      ml={expandable && i === 0 ? `${EXPAND_ARROW_WIDTH}px` : 0}
                      variant="bodySecondaryReg"
                    >
                      {headCell.label}
                    </Typography>
                    {showTooltipIcon && (
                      <TooltipIcon
                        title={headCell.tooltip}
                        enterDelay={1000}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </TableSortLabel>
                </TooltipWrapper>
              </TableCell>
            );
          }

          return (
            <TableCell
              key={headCell.id}
              sx={{ border: "none" }}
              align={headCell.align || "left"}
            >
              <Row
                alignItems="end"
                justifyContent={ALIGN_TO_FLEX[headCell.align || "left"]}
                sx={{ height: "18px" }}
                spacing={1}
              >
                <Typography
                  sx={{ fontWeight: 600, lineHeight: 18 / (14 * 1.25) }}
                  variant="bodySecondaryReg"
                  title={headCell.tooltip}
                >
                  {headCell.label}
                </Typography>
                {showTooltipIcon && (
                  <TooltipIcon title={headCell.tooltip} enterDelay={1000} />
                )}
              </Row>
            </TableCell>
          );
        })}
      </TableRow>
    </StyledTableHead>
  );
};

export default TableHead;
